// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  Download as DownloadIcon,
  ExpandMore,
} from '@/constants/hardfin-standard-icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Link,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

interface Field {
  label: string;
  csvHeader: string;
}

interface FieldCategory {
  id: string;
  label: string;
  fields: Field[];
  enabled: boolean; // TODO(#15945): Remove this flag once backend support is implemented
}

const fieldCategories: FieldCategory[] = [
  {
    id: 'advancedCosts',
    label: 'Include advanced cost breakdown',
    fields: [
      { label: 'Material cost - decimal', csvHeader: 'Bill of materials' },
      { label: 'Direct labor - decimal', csvHeader: 'Direct labor' },
      {
        label: 'Allocated indirect - decimal',
        csvHeader: 'Allocated indirect',
      },
      { label: 'Freight inbound - decimal', csvHeader: 'Freight inbound' },
      { label: 'Tax - decimal', csvHeader: 'Tax' },
      { label: 'Tariffs - decimal', csvHeader: 'Tariffs' },
      { label: 'Interest - decimal', csvHeader: 'Interest' },
      { label: 'Freight outbound - decimal', csvHeader: 'Freight outbound' },
      { label: 'Installation - decimal', csvHeader: 'Installation' },
    ],
    enabled: true,
  },
  {
    id: 'location',
    label: 'Include move (location change)',
    fields: [
      {
        label: 'Current location name - location name (required for moves)',
        csvHeader: 'Current location name',
      },
      {
        label: 'Current location customer - company name (optional)',
        csvHeader: 'Current location customer',
      },
      {
        label:
          'Location arrival date (delivery) - date (YYYY-MM-DD, required for moves)',
        csvHeader: 'Location arrival date (delivery)',
      },
      {
        label: 'Current location address - text (optional)',
        csvHeader: 'Current location address',
      },
    ],
    enabled: true,
  },
  {
    id: 'project',
    label: 'Include current project info',
    fields: [
      {
        label: 'Current project name - text (required)',
        csvHeader: 'Current project name',
      },
      {
        label:
          'Current project customer - company name (required for new projects)',
        csvHeader: 'Current project customer',
      },
      {
        label:
          'Assignment date on current project - date (YYYY-MM-DD, required)',
        csvHeader: 'Assignment date on current project',
      },
    ],
    enabled: true,
  },
  {
    id: 'sale',
    label: 'Include sale record',
    fields: [
      { label: 'Sale date - date (YYYY-MM-DD)', csvHeader: 'Sale date' },
      { label: 'Sale price - decimal (optional)', csvHeader: 'Sale price' },
      {
        label: 'Sale customer - company name (required)',
        csvHeader: 'Sale customer',
      },
    ],
    enabled: true,
  },
  {
    id: 'scrap',
    label: 'Include scrap detail',
    fields: [
      { label: 'Scrap date - date (YYYY-MM-DD)', csvHeader: 'Scrap date' },
      { label: 'Scrap reason - text (optional)', csvHeader: 'Scrap reason' },
      { label: 'Scrap value - decimal (optional)', csvHeader: 'Scrap value' },
    ],
    enabled: true,
  },
];

type CategoryToggles = Record<string, boolean>;

export function TemplateDownload({ onBack, onNext }: TemplateDownloadProps) {
  const [categoryToggles, setCategoryToggles] = useState<CategoryToggles>(
    () => {
      const initial: CategoryToggles = {};
      fieldCategories
        .filter((c) => c.enabled)
        .forEach((cat) => {
          initial[cat.id] = false;
        });
      return initial;
    }
  );

  const handleToggleChange = (categoryId: string) => {
    setCategoryToggles((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Asset serial',
      'Initial location',
      'Inventory date',
      'Status',
      'Description',
      'In service date',
      'Useful life',
      'Accounting currency',
      'Salvage value',
    ];

    // Add simple cost basis if advanced costs is not enabled
    if (!categoryToggles['advancedCosts']) {
      headers.push('Simple cost basis');
    }

    // Add headers from enabled and toggled-on categories
    fieldCategories
      .filter((c) => c.enabled)
      .forEach((category) => {
        if (categoryToggles[category.id]) {
          headers.push(...category.fields.map((f) => f.csvHeader));
        }
      });

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `asset_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [categoryToggles]);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Template for asset data</Typography>
          </Box>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}>
            <li>
              <Typography variant="body2">
                Asset serial - text (required)
              </Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Available fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">
                Initial location - location name
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Inventory date - date (YYYY-MM-DD)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Status - (FUNCTIONAL <i>or</i> NON_FUNCTIONAL)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Description - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                In service date - date (YYYY-MM-DD)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Useful life - integer (months)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Accounting currency -{' '}
                <Tooltip
                  title={
                    <>
                      Must be a valid ISO 4217 currency code
                      <br />
                      Examples:
                      <br />
                      USD - United States dollar
                      <br />
                      CAD - Canadian dollar
                      <br />
                      EUR - euro
                      <br />
                      GBP - British pound
                      <br />
                      <Link
                        href="https://en.wikipedia.org/wiki/ISO_4217"
                        target="_blank"
                        rel="noopener"
                        color="inherit"
                        underline="always"
                      >
                        See the full list of codes on Wikipedia
                      </Link>
                    </>
                  }
                  arrow
                >
                  <span
                    style={{
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    ISO code
                  </span>
                </Tooltip>{' '}
                (required when any monetary amount is provided)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Salvage value - decimal</Typography>
            </li>
            {!categoryToggles['advancedCosts'] && (
              <li>
                <Typography variant="body2">Cost basis - decimal</Typography>
              </li>
            )}
          </ul>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Optional field categories
            </Typography>

            {fieldCategories
              .filter((c) => c.enabled)
              .map((category) => (
                <Accordion
                  key={category.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '4px !important',
                    mb: 1,
                    '&:last-of-type': { mb: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      minHeight: 48,
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={categoryToggles[category.id]}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleChange(category.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                      }
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="body2">
                            {category.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({category.fields.length} fields)
                          </Typography>
                        </Box>
                      }
                      onClick={(e) => e.stopPropagation()}
                      sx={{ m: 0 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <ul
                      style={{
                        paddingLeft: '24px',
                        marginTop: 0,
                        marginBottom: 0,
                      }}
                    >
                      {category.fields.map((field) => (
                        <li key={field.csvHeader}>
                          <Typography variant="body2">{field.label}</Typography>
                        </li>
                      ))}
                    </ul>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Any fields included in your upload will overwrite existing data,
              including empty cells
            </li>
            <li>
              You can delete columns for fields you don't need to update. "Asset
              serial" is required. "Accounting currency" is required when a row
              includes a monetary amount.
            </li>
            <li>
              For targeted updates, only include the specific serials and fields
              you want to modify
            </li>
            <li>
              Date format must be YYYY-MM-DD (ex: '2020-06-25' for June 25,
              2020)
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
